import type {
  SourceContextScope,
  SourcePageContext,
  WxCopilotApplicantContext,
  WxCopilotApplicantTableContext,
  WxCopilotOpeningContext,
  WxCopilotPageContext,
  WxCopilotWorkflowContext,
} from '@/types/wx-copilot-context';

import { reportWxCopilotFailure } from './report-wx-copilot-failure';

type PageSpecificContext = Pick<
  WxCopilotPageContext,
  'workflow' | 'applicant' | 'applicantTable' | 'openings'
>;

class WxCopilotContextManager {
  private currentContext: WxCopilotPageContext | null = null;

  private pageSpecificContext: PageSpecificContext = {};

  private sourceContexts: Partial<
    Record<SourceContextScope, SourcePageContext>
  > = {};

  private debounceTimer: NodeJS.Timeout | null = null;

  private retryTimer: NodeJS.Timeout | null = null;

  private readonly DEBOUNCE_DELAY = 500;

  private readonly RETRY_DELAY = 200;

  private readonly MAX_RETRIES = 10;

  private mountId = 'wx-copilot-root';

  private pendingUrl: string | null = null;

  sendPageContext(url: string, mountId = 'wx-copilot-root'): void {
    this.mountId = mountId;
    this.pendingUrl = url;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.flushPageContext(url);
    }, this.DEBOUNCE_DELAY);
  }

  setSourceContext(
    scope: SourceContextScope,
    context: SourcePageContext | undefined,
  ): void {
    if (JSON.stringify(this.sourceContexts[scope]) === JSON.stringify(context))
      return;
    if (context) this.sourceContexts[scope] = context;
    else delete this.sourceContexts[scope];
    if (this.currentContext) this.sendContextUpdate();
  }

  private getSourceContext(): SourcePageContext {
    // Nested editors override their parent list; closing restores the parent.
    return Object.assign(
      {},
      ...(['opening', 'channels', 'campaigns', 'editor'] as const).map(
        scope => this.sourceContexts[scope],
      ),
    );
  }

  setWorkflowContext(workflow: WxCopilotWorkflowContext | undefined): void {
    const changed =
      JSON.stringify(this.pageSpecificContext.workflow) !==
      JSON.stringify(workflow);
    this.pageSpecificContext.workflow = workflow;
    if (changed && this.currentContext) {
      this.sendContextUpdate();
    }
  }

  setApplicantContext(applicant: WxCopilotApplicantContext | undefined): void {
    const changed =
      JSON.stringify(this.pageSpecificContext.applicant) !==
      JSON.stringify(applicant);
    this.pageSpecificContext.applicant = applicant;
    if (changed && this.currentContext) {
      this.sendContextUpdate();
    }
  }

  setApplicantTableContext(
    applicantTable: WxCopilotApplicantTableContext | undefined,
  ): void {
    const changed =
      JSON.stringify(this.pageSpecificContext.applicantTable) !==
      JSON.stringify(applicantTable);
    this.pageSpecificContext.applicantTable = applicantTable;
    if (changed && this.currentContext) {
      this.sendContextUpdate();
    }
  }

  setOpeningsContext(openings: WxCopilotOpeningContext[] | undefined): void {
    const changed =
      JSON.stringify(this.pageSpecificContext.openings) !==
      JSON.stringify(openings);
    this.pageSpecificContext.openings = openings;
    if (changed && this.currentContext) {
      this.sendContextUpdate();
    }
  }

  private sendContextUpdate(): void {
    if (!this.currentContext) return;

    this.flushPageContext(this.pendingUrl ?? this.currentContext.url);
  }

  private flushPageContext(url: string): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = null;
    this.pendingUrl = null;
    const newContext: WxCopilotPageContext = {
      url,
      title: typeof document !== 'undefined' ? document.title : undefined,
      timestamp: Date.now(),
      ...this.pageSpecificContext,
      ...this.getSourceContext(),
    };

    if (this.hasContextChanged(newContext)) {
      this.currentContext = newContext;
      window.wxCopilotPageContext = newContext;
      this.pushToCopilot(newContext);
    }
  }

  private pushToCopilot(context: WxCopilotPageContext, retryCount = 0): void {
    if (retryCount === 0 && this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    try {
      const copilotInstance =
        window.WxCopilotComponentInstances?.[this.mountId];

      if (copilotInstance?.updateEphemeralContext) {
        const contextPayload: Record<string, unknown> = {
          page: {
            url: context.url,
            title: context.title,
            timestamp: context.timestamp,
          },
        };

        if (context.workflow) {
          contextPayload.workflow = {
            id: context.workflow.workflowId,
            name: context.workflow.workflowName,
            currentStageId: context.workflow.currentStageId,
            currentStageName: context.workflow.currentStageName,
          };
        }

        if (context.applicant) {
          contextPayload.applicant = {
            id: context.applicant.applicantId,
            name: context.applicant.applicantName,
            email: context.applicant.applicantEmail,
          };
        }

        if (context.applicantTable) {
          contextPayload.applicantTable = {
            opening: {
              id: context.applicantTable.openingId,
              name: context.applicantTable.openingName,
            },
            currentStage: context.applicantTable.currentStageId
              ? {
                  id: context.applicantTable.currentStageId,
                  title: context.applicantTable.currentStageName,
                  type: context.applicantTable.currentStageType,
                }
              : undefined,
            stages: context.applicantTable.stages,
            applicants: context.applicantTable.applicants,
            totalApplicants: context.applicantTable.totalApplicants,
          };
        }

        if (context.openings && context.openings.length > 0) {
          contextPayload.openings = context.openings.map(opening => ({
            id: opening.id,
            name: opening.name,
          }));
        }

        if (context.sourceOpening)
          contextPayload.sourceOpening = context.sourceOpening;
        if (context.sourceCampaign)
          contextPayload.sourceCampaign = context.sourceCampaign;
        if (context.sourceChannel)
          contextPayload.sourceChannel = context.sourceChannel;

        (copilotInstance.updateEphemeralContext as (context: string) => void)(
          JSON.stringify(contextPayload),
        );
      } else if (retryCount < this.MAX_RETRIES) {
        if (this.retryTimer) {
          clearTimeout(this.retryTimer);
        }
        this.retryTimer = setTimeout(() => {
          this.pushToCopilot(context, retryCount + 1);
        }, this.RETRY_DELAY);
      }
    } catch (error) {
      reportWxCopilotFailure('update', error);
    }
  }

  private hasContextChanged(newContext: WxCopilotPageContext): boolean {
    if (!this.currentContext) {
      return true;
    }

    return (
      this.currentContext.url !== newContext.url ||
      JSON.stringify(this.currentContext.workflow) !==
        JSON.stringify(newContext.workflow) ||
      JSON.stringify(this.currentContext.applicant) !==
        JSON.stringify(newContext.applicant) ||
      JSON.stringify(this.currentContext.applicantTable) !==
        JSON.stringify(newContext.applicantTable) ||
      JSON.stringify(this.currentContext.openings) !==
        JSON.stringify(newContext.openings) ||
      JSON.stringify(this.currentContext.sourceOpening) !==
        JSON.stringify(newContext.sourceOpening) ||
      JSON.stringify(this.currentContext.sourceCampaign) !==
        JSON.stringify(newContext.sourceCampaign) ||
      JSON.stringify(this.currentContext.sourceChannel) !==
        JSON.stringify(newContext.sourceChannel)
    );
  }

  clearPageSpecificContext(): void {
    this.pageSpecificContext = {};
    this.sourceContexts = {};
  }

  cleanup(): void {
    this.pendingUrl = null;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }
}

export const wxCopilotContextManager = new WxCopilotContextManager();
